
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface OrderItem {
  id: number;
  artwork_title: string;
  artwork_artist: string;
  artwork_size: string;
  artwork_price: number;
  artwork_image_url: string;
}

interface OrderStatusHistory {
  id: number;
  status: string;
  status_date: string;
  notes: string;
}

interface OrderStatus {
  id: number;
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_city: string;
  status: 'preparing' | 'shipped' | 'delivered' | 'cancelled';
  order_date: string;
  estimated_delivery: string;
  tracking_number?: string;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  payment_method: string;
  notes: string;
  items: OrderItem[];
  status_history: OrderStatusHistory[];
}

export default function OrderTrackingPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [orderData, setOrderData] = useState<OrderStatus | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [recentOrders, setRecentOrders] = useState<string[]>([]);

  useEffect(() => {
    fetchRecentOrderNumbers();
  }, []);

  const fetchRecentOrderNumbers = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('order_number')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      const orderNumbers = data?.map(order => order.order_number) || [];
      setRecentOrders(orderNumbers);
    } catch (error) {
      console.error('Error fetching recent orders:', error);
      // Fallback to mock data
      setRecentOrders(['NOX2024001', 'NOX2024002', 'NOX2024003']);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderNumber.trim()) {
      setError('Lütfen sipariş numaranızı girin');
      return;
    }

    setIsSearching(true);
    setError('');
    
    try {
      // Sipariş bilgilerini getir
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', orderNumber.toUpperCase())
        .single();

      if (orderError) throw orderError;

      if (!orderData) {
        setError('Sipariş bulunamadı. Lütfen sipariş numaranızı kontrol edin.');
        setOrderData(null);
        return;
      }

      // Sipariş ürünlerini getir
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select('*')
        .eq('order_id', orderData.id);

      if (itemsError) throw itemsError;

      // Sipariş durumu geçmişini getir
      const { data: historyData, error: historyError } = await supabase
        .from('order_status_history')
        .select('*')
        .eq('order_id', orderData.id)
        .order('status_date', { ascending: true });

      if (historyError) throw historyError;

      // Verileri birleştir
      const completeOrderData: OrderStatus = {
        ...orderData,
        items: itemsData || [],
        status_history: historyData || []
      };

      setOrderData(completeOrderData);
      setError('');
    } catch (error) {
      console.error('Error fetching order:', error);
      setError('Sipariş bulunamadı. Lütfen sipariş numaranızı kontrol edin.');
      setOrderData(null);
    } finally {
      setIsSearching(false);
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'preparing':
        return {
          text: 'Hazırlanıyor',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          icon: 'ri-time-line'
        };
      case 'shipped':
        return {
          text: 'Kargoya Verildi',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          icon: 'ri-truck-line'
        };
      case 'delivered':
        return {
          text: 'Teslim Edildi',
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: 'ri-check-line'
        };
      case 'cancelled':
        return {
          text: 'İptal Edildi',
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: 'ri-close-line'
        };
      default:
        return {
          text: 'Bilinmiyor',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          icon: 'ri-question-line'
        };
    }
  };

  const getProgressSteps = (status: string) => {
    const steps = [
      { key: 'preparing', label: 'Sipariş Alındı', icon: 'ri-shopping-bag-line' },
      { key: 'shipped', label: 'Kargoya Verildi', icon: 'ri-truck-line' },
      { key: 'delivered', label: 'Teslim Edildi', icon: 'ri-check-line' }
    ];

    const currentIndex = steps.findIndex(step => step.key === status);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      active: index === currentIndex
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price: number) => {
    return `${price.toLocaleString('tr-TR')} TL`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-8">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span>Ana Sayfa</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Sipariş Takibi</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-palette-line text-2xl text-gray-700"></i>
              </div>
              <span className="text-xl font-bold" style={{ fontFamily: 'Pacifico, serif' }}>
                Nox Sanat
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-blue-100 rounded-full">
              <i className="ri-search-line text-3xl text-blue-600"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Siparişinizi Takip Edin</h2>
            <p className="text-gray-600">
              Sipariş numaranızı girerek siparişinizin durumunu öğrenebilirsiniz
            </p>
          </div>

          <form onSubmit={handleSearch} className="max-w-md mx-auto">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sipariş Numarası
              </label>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="örn: NOX2024001"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                disabled={isSearching}
              />
            </div>

            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSearching}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {isSearching ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Aranıyor...
                </div>
              ) : (
                'Sipariş Ara'
              )}
            </button>
          </form>

          {recentOrders.length > 0 && (
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 mb-2">Son siparişler:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {recentOrders.map((orderNum) => (
                  <button
                    key={orderNum}
                    onClick={() => setOrderNumber(orderNum)}
                    className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    {orderNum}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Order Details */}
        {orderData && (
          <div className="space-y-6">
            {/* Order Status */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Sipariş #{orderData.order_number}</h3>
                  <p className="text-gray-600">Sipariş Tarihi: {formatDate(orderData.order_date)}</p>
                  <p className="text-gray-600">Müşteri: {orderData.customer_name}</p>
                </div>
                <div className={`px-4 py-2 rounded-full ${getStatusInfo(orderData.status).bgColor}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${getStatusInfo(orderData.status).icon} ${getStatusInfo(orderData.status).color}`}></i>
                    <span className={`font-medium ${getStatusInfo(orderData.status).color}`}>
                      {getStatusInfo(orderData.status).text}
                    </span>
                  </div>
                </div>
              </div>

              {/* Progress Steps */}
              {orderData.status !== 'cancelled' && (
                <div className="mb-6">
                  <div className="flex items-center justify-between relative">
                    {getProgressSteps(orderData.status).map((step, index) => (
                      <div key={step.key} className="flex flex-col items-center flex-1 relative z-10">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${
                          step.completed 
                            ? 'bg-blue-600 border-blue-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-400'
                        }`}>
                          <i className={`${step.icon} text-lg`}></i>
                        </div>
                        <span className={`text-xs mt-2 text-center ${
                          step.completed ? 'text-blue-600 font-medium' : 'text-gray-500'
                        }`}>
                          {step.label}
                        </span>
                      </div>
                    ))}
                    {/* Progress Line */}
                    <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-300 -z-10">
                      <div 
                        className="h-full bg-blue-600 transition-all duration-500"
                        style={{ 
                          width: orderData.status === 'preparing' ? '0%' : 
                                 orderData.status === 'shipped' ? '50%' : '100%' 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Tahmini Teslimat</p>
                  <p className="text-lg font-bold text-gray-900">{formatDate(orderData.estimated_delivery)}</p>
                </div>
                {orderData.tracking_number && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Kargo Takip No</p>
                    <p className="text-lg font-bold text-gray-900">{orderData.tracking_number}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Sipariş Detayları</h4>
              <div className="space-y-4">
                {orderData.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                    <img
                      src={item.artwork_image_url}
                      alt={item.artwork_title}
                      className="w-20 h-24 object-cover object-top rounded-lg"
                    />
                    <div className="flex-1">
                      <h5 className="font-bold text-gray-900">{item.artwork_title}</h5>
                      <p className="text-gray-600">{item.artwork_artist}</p>
                      <p className="text-gray-500 text-sm">{item.artwork_size}</p>
                      <p className="font-bold text-lg text-gray-900 mt-1">{formatPrice(item.artwork_price)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ara Toplam:</span>
                    <span className="font-medium">{formatPrice(orderData.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">KDV:</span>
                    <span className="font-medium">{formatPrice(orderData.tax_amount)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                    <span>Toplam:</span>
                    <span>{formatPrice(orderData.total_amount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Status History */}
            {orderData.status_history.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">Sipariş Geçmişi</h4>
                <div className="space-y-4">
                  {orderData.status_history.map((history, index) => (
                    <div key={index} className="flex items-start gap-4 pb-4 border-b border-gray-200 last:border-b-0">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full ${getStatusInfo(history.status).bgColor}`}>
                        <i className={`${getStatusInfo(history.status).icon} text-sm ${getStatusInfo(history.status).color}`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{getStatusInfo(history.status).text}</span>
                          <span className="text-sm text-gray-500">{formatDateTime(history.status_date)}</span>
                        </div>
                        {history.notes && (
                          <p className="text-sm text-gray-600">{history.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipping Address */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Teslimat Bilgileri</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Teslimat Adresi</h5>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-900">{orderData.customer_name}</p>
                    <p className="text-gray-600">{orderData.shipping_address}</p>
                    <p className="text-gray-600">{orderData.shipping_city}</p>
                    <p className="text-gray-600">{orderData.customer_phone}</p>
                  </div>
                </div>
                <div>
                  <h5 className="font-medium text-gray-700 mb-2">Ödeme Bilgileri</h5>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-600">Ödeme Yöntemi: {orderData.payment_method}</p>
                    <p className="text-gray-600">E-posta: {orderData.customer_email}</p>
                    {orderData.notes && (
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Notlar:</p>
                        <p className="text-sm text-gray-600">{orderData.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-customer-service-line text-xl text-blue-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Yardıma mı ihtiyacınız var?</h4>
                  <p className="text-gray-600 mb-4">
                    Siparişinizle ilgili herhangi bir sorunuz varsa bizimle iletişime geçin.
                  </p>
                  <button
                    onClick={() => {
                      window.REACT_APP_NAVIGATE('/');
                      setTimeout(() => {
                        const contactSection = document.getElementById('contact');
                        if (contactSection) {
                          contactSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }, 100);
                    }}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    İletişime Geç
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
